import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Link, useHistory } from "react-router-dom";
import NoResults from "../../../../../components/NoResults/NoResults";
import SearchFilter from "../../../../../components/Filter/SearchFilter";
import useFetch from "../../../../../hooks/fetchMSSQL-hook";
import exportReportXlsx from "../../utils/exportReportXlsx";
import { getReportMachineInfo, matchesReportFilters, hasReportFilters, reportFilterOptions } from "../../utils/machineReportFilters";

const extractSerialFromFullName = (fullName) => {
  if (!fullName) return null;
  const m = String(fullName).match(/\(([^()]+)\)\s*$/);
  return m?.[1]?.trim() || null;
};

const getSerialFromMachine = (m) => {
  return (
    (m?.SerialNo && String(m.SerialNo).trim()) ||
    (m?.MachineSerialNo && String(m.MachineSerialNo).trim()) ||
    extractSerialFromFullName(m?.MachineFullName) ||
    null
  );
};

const isMachineActive = (m) => {
  if (m?.IsActive === undefined || m?.IsActive === null) return true;
  if (typeof m.IsActive === "boolean") return m.IsActive;
  return Boolean(Number(m.IsActive));
};

const toPosInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return i > 0 ? i : null;
};

const getValue = (obj, keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && String(obj[key]).trim() !== "") {
      return String(obj[key]).trim();
    }
  }

  return null;
};

const getChoiceNo = (r) => {
  return getValue(r, [
    "MachineFeederNo",
    "FeederNo",
    "ChoiceNo",
    "SelectionNo",
    "MachineChoiceNo",
    "VendNo",
    "VMCChoiceNo",
    "ProductNo",
  ]);
};

const getMDBCode = (r) => {
  return getValue(r, [
    "MDBCode",
    "MDB",
    "MDBNo",
    "MDBProductNo",
    "TrxProductNo",
    "ProductNo",
  ]);
};

const getMachineInfo = getReportMachineInfo;

const DEFAULT_REPORT_FILTER = {
  reportMinDays: 90,
  reportLimit: 100,
  reportMachine: "",
  machineTags: { id: [], name: [] },
  reportRegion: "",
  reportRoute: "",
};

const resolveMachineId = (r) => toPosInt(r?.MachineId);
const resolveTerminalId = (r) => toPosInt(r?.TerminalId);

const getTerminalSerialFromRow = (r) => {
  return (r?.TerminalSerialNo && String(r.TerminalSerialNo).trim()) || null;
};

const getSerialFromRow = (r) => {
  return (
    (r?.MachineSerialNo && String(r.MachineSerialNo).trim()) ||
    (r?.SerialNo && String(r.SerialNo).trim()) ||
    getTerminalSerialFromRow(r) ||
    extractSerialFromFullName(r?.MachineFullName) ||
    null
  );
};

const hasMissingMachineAssignment = (r) => Boolean(Number(r?.MissingMachineAssignment || 0));

const getLabelFromRow = (r) => {
  if (hasMissingMachineAssignment(r)) {
    const terminalSerial = getTerminalSerialFromRow(r);
    return terminalSerial
      ? `Terminal ${terminalSerial} bez przypisanego automatu`
      : "Terminal bez przypisanego automatu";
  }

  const mf = r?.MachineFullName ? String(r.MachineFullName).trim() : null;
  if (mf) return mf;

  const name = r?.MachineName ? String(r.MachineName).trim() : null;
  const serial = getSerialFromRow(r);

  if (name && serial) return `${name} (${serial})`;
  if (serial) return `? (${serial})`;
  return "-";
};

const getUnknownLabelForDatalist = (r) => {
  const serial = getSerialFromRow(r);
  const base = getLabelFromRow(r);
  if (!serial) return base || "-";

  const b = String(base || "").trim();
  if (!b || b === "-") return serial;
  if (b.startsWith(serial)) return b;

  return `${serial} — ${b}`;
};

const getMachineIdsForTags = (fetchMssqlApi, tagIds) => new Promise((resolve, reject) => {
    fetchMssqlApi(
        "tags/getMachineIdsByTags",
        { method: "POST", hideNotification: true, data: { machineTagIds: tagIds } },
        (data) => {
            if (!Array.isArray(data)) {
                reject(new Error("Nieprawidłowa odpowiedź podczas pobierania automatów według tagów"));
                return;
            }
            resolve(new Set(data.map(Number).filter(Number.isFinite)));
        },
        reject
    );
});

export default function UnassignedChoicesChart({ machines = [], products = [] }) {
  const { fetchMssqlApi } = useFetch();
  const history = useHistory();

  const DBG_VERBOSE = useMemo(() => {
    try {
      return localStorage.getItem("DBG_UNASSIGNED_CHOICES") === "1";
    } catch {
      return false;
    }
  }, []);

  const dbg = (...args) => {
    if (!DBG_VERBOSE) return;
    console.log("[UnassignedChoicesChart]", ...args);
  };
  const dbgWarn = (...args) => {
    console.warn("[UnassignedChoicesChart]", ...args);
  };
  const dbgErr = (...args) => {
    console.error("[UnassignedChoicesChart]", ...args);
  };

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    dbg("MOUNT", { machinesLen: machines?.length || 0, productsLen: products?.length || 0 });
    return () => {
      mountedRef.current = false;
      dbg("UNMOUNT");
    };
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportPayloadRef = useRef(null);

  const requestIdRef = useRef(0);

  const allActiveSerials = useMemo(() => {
    const arr = (machines || []).filter(isMachineActive).map(getSerialFromMachine).filter(Boolean);
    dbg("allActiveSerials", { count: arr.length, sample: arr.slice(0, 5) });
    return arr;
  }, [machines]);

  const activeSerialSet = useMemo(() => new Set(allActiveSerials), [allActiveSerials]);

  const productsByEAN = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      if (p?.EAN) map.set(String(p.EAN), p.Name);
    });
    dbg("productsByEAN built", { size: map.size });
    return map;
  }, [products]);

  const machineInfoBySerial = useMemo(() => {
    const map = new Map();

    (machines || []).forEach((m) => {
      const serial = getSerialFromMachine(m);

      if (serial) {
        map.set(serial, getMachineInfo(m));
      }
    });

    return map;
  }, [machines]);

  const renderMachineInfo = (r) => {
    const serial = getSerialFromRow(r);
    const fromMachine = serial ? machineInfoBySerial.get(serial) : null;
    const fromRow = getMachineInfo(r);

    const tags = fromRow.tags || fromMachine?.tags;
    const region = fromRow.region || fromMachine?.region;
    const route = fromRow.route || fromMachine?.route;

    if (!tags && !region && !route) return "-";

    return (
      <div className="small">
        {tags && <div><span className="text-muted">Tagi: </span>{tags}</div>}
        {region && <div><span className="text-muted">Rejon: </span>{region}</div>}
        {route && <div><span className="text-muted">Trasa: </span>{route}</div>}
      </div>
    );
  };

  const unknownMachineOptions = useMemo(() => {
    const seenValues = new Set();
    const out = [];

    (rows || []).forEach((r) => {
      const machineId = resolveMachineId(r);
      if (machineId) return;

      const serial = getSerialFromRow(r);
      if (!serial) return;

      const v1 = String(serial).trim();
      const v2 = getUnknownLabelForDatalist(r);

      if (v1 && !seenValues.has(v1)) {
        seenValues.add(v1);
        out.push({ value: v1, serial });
      }
      if (v2 && v2 !== "-" && !seenValues.has(v2)) {
        seenValues.add(v2);
        out.push({ value: v2, serial });
      }
    });

    out.sort((a, b) => String(a.value).localeCompare(String(b.value)));

    dbg("unknownMachineOptions", { count: out.length, sample: out[0] || null });
    return out;
  }, [rows]);

  const machineNameToSerial = useMemo(() => {
    const map = new Map();

    (machines || []).forEach((m) => {
      const serial = getSerialFromMachine(m);
      const name = m?.Name ? String(m.Name).trim() : null;

      if (serial) map.set(String(serial), String(serial));
      if (name && serial) map.set(`${name} (${serial})`, serial);

      const mf = m?.MachineFullName ? String(m.MachineFullName).trim() : null;
      if (mf) {
        const s2 = extractSerialFromFullName(mf);
        if (s2) map.set(mf, s2);
      }
    });

    unknownMachineOptions.forEach((u) => {
      if (u?.value && u?.serial) map.set(u.value, u.serial);
    });

    dbg("machineNameToSerial built", { size: map.size });
    return map;
  }, [machines, unknownMachineOptions]);

  const reportOptions = useMemo(() => ({
    machines: [
      ...(machines || []).map((m) => {
        const serial = getSerialFromMachine(m);
        const name = m?.Name ? String(m.Name).trim() : null;
        return (name && serial && `${name} (${serial})`) || m?.MachineFullName || serial || null;
      }).filter(Boolean),
      ...unknownMachineOptions.map((u) => u.value),
    ],
    regions: reportFilterOptions(machines, "region"),
    routes: reportFilterOptions(machines, "route"),
  }), [machines, unknownMachineOptions]);

  const resolveSerialFromMachineFilterValue = useCallback(
    (value) => {
      const v = String(value || "").trim();
      if (!v) return null;

      let serial = machineNameToSerial.get(v) || null;

      if (!serial) serial = extractSerialFromFullName(v);

      if (!serial && /^[A-Za-z0-9_-]{4,}$/.test(v)) {
        serial = v;
      }

      return serial || null;
    },
    [machineNameToSerial]
  );

  const fetchUnassignedChoices = useCallback(
    (reportFilter = DEFAULT_REPORT_FILTER) => {
      const reqId = ++requestIdRef.current;
      exportPayloadRef.current = null;
      const filterValue = String(reportFilter.reportMachine || "").trim();
      const hasMachineFilter = Boolean(filterValue);
      const resolvedSerial = hasMachineFilter
        ? resolveSerialFromMachineFilterValue(filterValue)
        : null;

      if (hasMachineFilter && !resolvedSerial) {
        if (!mountedRef.current) return;
        setRows([]);
        setLoading(false);
        dbgWarn("Machine filter provided but serial could not be resolved", { filterValue });
        return;
      }

      const metadataFilters = {
        region: String(reportFilter.reportRegion || ""),
        route: String(reportFilter.reportRoute || ""),
      };
      const filteredByMetadata = hasReportFilters(metadataFilters);
      const tagIds = Array.isArray(reportFilter.machineTags?.id)
        ? [...new Set(reportFilter.machineTags.id.map(Number).filter(id => Number.isInteger(id) && id > 0))]
        : [];
      const filteredByTags = tagIds.length > 0;

      const sendReport = (taggedMachineIds = null) => {
        if (reqId !== requestIdRef.current || !mountedRef.current) return;

        const matchingSerials = filteredByMetadata || filteredByTags
          ? Array.from(new Set((machines || []).filter(isMachineActive)
              .filter(m => matchesReportFilters(m, metadataFilters))
              .filter(m => !taggedMachineIds || taggedMachineIds.has(Number(m?.MachineId)))
              .map(getSerialFromMachine).filter(Boolean)))
          : null;
        const requestedSerials = resolvedSerial
          ? (matchingSerials && !matchingSerials.includes(resolvedSerial) ? [] : [resolvedSerial])
          : matchingSerials;

        if (requestedSerials && requestedSerials.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }

        const payload = {
          machineSerialNos: requestedSerials,
          includeUnknown: !hasMachineFilter && !filteredByMetadata && !filteredByTags,
          minDays: Math.max(1, Math.min(3650, Math.trunc(Number(reportFilter.reportMinDays) || 90))),
          limit: Math.max(1, Math.min(5000, Math.trunc(Number(reportFilter.reportLimit) || 100))),
        };

        console.log("[UnassignedChoicesChart] FETCH", payload);

        if (mountedRef.current) setLoading(true);
        dbg("request start", { reqId, payload });

        fetchMssqlApi(
          "stat/unassigned-choices",
          {
            method: "POST",
            hideNotification: true,
            showLoader: true,
            data: payload,
          },
          (data) => {
            if (reqId !== requestIdRef.current) {
              dbgWarn("stale response ignored", { reqId, latest: requestIdRef.current });
              return;
            }
            if (!mountedRef.current) return;

            let arr = Array.isArray(data) ? data : [];

            if (!hasMachineFilter) {
              arr = arr.filter((r) => {
                const mid = resolveMachineId(r);
                if (!mid) return true;
                const serial = getSerialFromRow(r);
                if (!serial) return true;
                return activeSerialSet.has(String(serial));
              });
            }

            dbg("response OK", { reqId, len: arr.length, sample: arr[0] || null });

            arr.sort((a, b) => {
              const ta = new Date(a?.LastTrxDateTime || 0).getTime();
              const tb = new Date(b?.LastTrxDateTime || 0).getTime();
              if (tb !== ta) return tb - ta;
              const sa = String(a?.MachineSerialNo || "");
              const sb = String(b?.MachineSerialNo || "");
              if (sa !== sb) return sa.localeCompare(sb);
              return Number(a?.ProductNo || 0) - Number(b?.ProductNo || 0);
            });

            setRows(arr);
            // Także eksport pomija nieaktywne przypisane automaty (tak jak tabela).
            exportPayloadRef.current = arr.length
              ? { ...payload, onlyActiveMachines: !hasMachineFilter,
                  activeMachineSerialNos: !hasMachineFilter ? allActiveSerials : [] }
              : null;
            setLoading(false);
          },
          (err) => {
            if (reqId !== requestIdRef.current) return;
            if (!mountedRef.current) return;
            console.log("[UnassignedChoicesChart] FETCH ERROR", { reqId, err, payload });
            setRows([]);
            setLoading(false);
            dbgErr("fetch error details", err);
          }
        );
      };

      if (filteredByTags) {
        if (mountedRef.current) setLoading(true);
        getMachineIdsForTags(fetchMssqlApi, tagIds)
          .then(sendReport)
          .catch(err => {
            if (reqId !== requestIdRef.current || !mountedRef.current) return;
            dbgErr("[UnassignedChoicesChart] Błąd filtrowania tagów automatów", err);
            setRows([]);
            setLoading(false);
          });
      } else {
        sendReport();
      }
    },
    [
      fetchMssqlApi,
      activeSerialSet,
      resolveSerialFromMachineFilterValue,
      machines,
    ]
  );

  const openTerminalAssignment = (terminalSerial, terminalId) => {
    const serial = String(terminalSerial || "").trim();
    const id = toPosInt(terminalId);

    if (!serial && !id) return;

    if (serial) {
      sessionStorage.setItem("openTerminalAssignSerialNo", serial);
    } else {
      sessionStorage.removeItem("openTerminalAssignSerialNo");
    }

    if (id) {
      sessionStorage.setItem("openTerminalAssignTerminalId", String(id));
    } else {
      sessionStorage.removeItem("openTerminalAssignTerminalId");
    }

    sessionStorage.setItem("openTerminalAssignModal", "1");

    history.push("/terminals");
  };

  const formatLastTrx = (dt) => {
    if (!dt) return "-";
    const m = moment(dt);
    if (!m.isValid()) return String(dt);
    const days = moment().diff(m, "days");
    return `${m.format("YYYY-MM-DD HH:mm:ss")} (${days} dni temu)`;
  };

  const resolveEAN = (r) => {
    if (r?.TrxEAN !== undefined && r?.TrxEAN !== null) return String(r.TrxEAN);
    if (r?.EAN !== undefined && r?.EAN !== null) return String(r.EAN);
    if (r?.ProductEAN !== undefined && r?.ProductEAN !== null) return String(r.ProductEAN);
    return null;
  };

  const resolveProductName = (r, eanStr) => {
    if (r?.ProductName) return r.ProductName;
    if (eanStr) return productsByEAN.get(String(eanStr)) || null;
    return null;
  };

  const rowsSorted = useMemo(() => {
    const arr = Array.isArray(rows) ? rows : [];

    return arr.filter((r) => {
      if (hasMissingMachineAssignment(r)) return true;

      const machineId = resolveMachineId(r);
      if (!machineId) return true;

      const hasMP = Boolean(Number(r?.HasMachineProduct || 0)) || Boolean(r?.MachineProductId);
      const isDefaultProduct = String(r?.ProductEAN || "") === "1";

      return !hasMP || isDefaultProduct;
    });
  }, [rows]);

  const exportToExcel = () => {
    if (loading || exporting || !exportPayloadRef.current) return;
    setExporting(true);
    exportReportXlsx(fetchMssqlApi, "stat/unassigned-choices/export", exportPayloadRef.current, () => setExporting(false));
  };

  const isEmpty = !rowsSorted || rowsSorted.length === 0;

  return (
    <div className="m-2">
      <SearchFilter
        filterName="unassignedChoicesReport"
        defaultFilter={DEFAULT_REPORT_FILTER}
        search={fetchUnassignedChoices}
        reportOptions={reportOptions}
        forNow
        toolbarAction={
          <button type="button" className="btn text-primary d-inline-flex align-items-center p-0" onClick={exportToExcel}
            disabled={loading || exporting || isEmpty}>
            <i className="fas fa-file-export mr-1" />Export
          </button>
        }
      />
      <div className="card overflow-auto">
        <div
          className="card-body overflow-auto p-0 custom-scroll border"
          style={{ maxHeight: window.innerHeight - 268, overflowY: "auto" }}
        >
          {isEmpty ? (
            <div className="p-3">
              <NoResults />
            </div>
          ) : (
            <table className="table table-striped mb-0">
              <thead>
                <tr className="headers-stick-trxs">
                  <th className="p-2 pl-3" style={{ minWidth: 260 }}>
                    Automat
                  </th>
                  <th className="p-2 text-center" style={{ width: 110 }}>
                    Nr wyboru
                  </th>
                  <th className="p-2 text-center" style={{ width: 110 }} title="To jest kod MDB, a nie numer wyboru z maszyny">
                    Kod MDB
                  </th>
                  <th className="p-2" style={{ minWidth: 320 }}>
                    Nazwa produktu / EAN
                  </th>
                  <th className="p-2" style={{ minWidth: 220 }}>
                    Tagi / Rejon / Trasa
                  </th>
                  <th className="p-2" style={{ minWidth: 240 }}>
                    Ostatnia transakcja
                  </th>
                  <th className="p-2 text-center" style={{ width: 220 }}>
                    Akcja
                  </th>
                </tr>
              </thead>

              <tbody>
                {rowsSorted.map((r, idx) => {
                  const needsTerminalAssignment = hasMissingMachineAssignment(r);
                  const machineId = resolveMachineId(r);
                  const terminalId = resolveTerminalId(r);
                  const machineLabel = getLabelFromRow(r);
                  const terminalSerial = getTerminalSerialFromRow(r);
                  const choiceNo = needsTerminalAssignment ? "-" : getChoiceNo(r) ?? "-";
                  const mdbCode = needsTerminalAssignment ? "-" : getMDBCode(r) ?? "-";

                  const eanStr = resolveEAN(r);
                  const productName = resolveProductName(r, eanStr);

                  const hasMP =
                    Boolean(Number(r?.HasMachineProduct || 0)) || Boolean(r?.MachineProductId);

                  const isMissingMapping = !hasMP;
                  const isDefaultProduct = String(r?.ProductEAN || "") === "1";

                  const serialForUnknown = getSerialFromRow(r);

                  const machineCell = needsTerminalAssignment ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-link text-left"
                      style={{ whiteSpace: "normal" }}
                      title="Przejdź do przypisania terminala do automatu"
                      onClick={() => openTerminalAssignment(terminalSerial, terminalId)}
                    >
                      <div className="d-flex flex-column">
                        <span>{machineLabel}</span>
                      </div>
                    </button>
                  ) : machineId ? (
                    <Link
                      to={`/machine/${machineId}`}
                      className="btn btn-sm btn-outline-link text-left"
                      style={{ whiteSpace: "normal" }}
                    >
                      {machineLabel}
                    </Link>
                  ) : (
                    <Link
                      to={`/machine/0?tab=new${
                        serialForUnknown ? `&serial=${encodeURIComponent(serialForUnknown)}` : ""
                      }`}
                      className="btn btn-sm btn-outline-link text-left"
                      style={{ whiteSpace: "normal" }}
                      title="Brak w bazie — przejdź do dodawania automatu"
                    >
                      {machineLabel}
                    </Link>
                  );

                  let actionEl = null;

                  if (needsTerminalAssignment) {
                    actionEl = (
                      <button
                        type="button"
                        className="btn btn-sm btn-warning font-fix"
                        onClick={() => openTerminalAssignment(terminalSerial, terminalId)}
                      >
                        Przypisz terminal
                      </button>
                    );
                  } else if (!machineId) {
                    actionEl = (
                      <Link
                        to={`/machine/0?tab=new${
                          serialForUnknown ? `&serial=${encodeURIComponent(serialForUnknown)}` : ""
                        }`}
                        className="btn btn-sm btn-danger font-fix"
                      >
                        Dodaj automat
                      </Link>
                    );
                  } else if (isMissingMapping || isDefaultProduct) {
                    actionEl = (
                      <Link to={`/machine/${machineId}`} className="btn btn-sm btn-danger font-fix">
                        Konfiguracja
                      </Link>
                    );
                  }

                  return (
                    <tr key={idx}>
                      <td className="p-2">{machineCell}</td>
                      <td className="p-2 text-center">{choiceNo}</td>
                      <td className="p-2 text-center" title="To jest kod MDB, a nie numer wyboru z maszyny">
                        {mdbCode}
                      </td>

                      <td className="p-2">
                        {needsTerminalAssignment ? (
                          <div className="d-flex flex-column">
                            <div>Przypisz terminal do automatu</div>
                            {terminalSerial && (
                              <div className="text-muted small">Terminal: {terminalSerial}</div>
                            )}
                          </div>
                        ) : (
                          <div className="d-flex flex-column">
                            <div>{productName || (eanStr ? `EAN: ${eanStr}` : "-")}</div>

                            {eanStr && (
                              <div className="text-muted small">
                                EAN: {eanStr}
                                {isDefaultProduct && (
                                  <span
                                    className="badge badge-warning ml-2"
                                    title="tblMP => tblProduct.EAN = 1"
                                  >
                                    Produkt domyślny
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="p-2">{renderMachineInfo(r)}</td>
                      <td className="p-2 text-nowrap">{formatLastTrx(r?.LastTrxDateTime)}</td>
                      <td className="p-2 text-center">{actionEl}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}